/**
 * The day list — a collapsible sidebar on desktop, a bottom sheet on mobile.
 * Renders the "All days" option plus one row per day, and reports selection
 * changes back to the caller.
 */

import { ALL_DAYS, type DaySelection, type Itinerary, CATEGORIES } from '../lib/types';
import { categoryColor, categoryLabelHe } from '../lib/colors';

export interface DayListController {
  /** Reflect the current selection in the list's active state. */
  setActive(selection: DaySelection): void;
}

function shortDate(iso: string): string {
  const m = iso.match(/^\d{4}-(\d{2})-(\d{2})$/);
  if (!m) return iso;
  return `${Number(m[2])}/${Number(m[1])}`; // D/M
}

function colorDot(color: string): HTMLElement {
  const dot = document.createElement('span');
  dot.className = 'day-dot';
  dot.style.background = color;
  dot.setAttribute('aria-hidden', 'true');
  return dot;
}

/** Render the legend of category colors. */
export function renderLegend(container: HTMLElement): void {
  container.replaceChildren();
  for (const cat of CATEGORIES) {
    const item = document.createElement('span');
    item.className = 'legend__item';
    item.appendChild(colorDot(categoryColor(cat)));
    const label = document.createElement('span');
    label.textContent = categoryLabelHe(cat);
    item.appendChild(label);
    container.appendChild(item);
  }
}

/** Render the day list and wire selection. Returns a small controller. */
export function renderDayList(
  container: HTMLElement,
  itinerary: Itinerary,
  onSelect: (selection: DaySelection) => void,
): DayListController {
  container.replaceChildren();
  const buttons = new Map<DaySelection, HTMLButtonElement>();

  const makeRow = (selection: DaySelection): HTMLButtonElement => {
    const li = document.createElement('li');
    li.className = 'day-list__li';
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'day-item';
    btn.addEventListener('click', () => onSelect(selection));
    li.appendChild(btn);
    container.appendChild(li);
    buttons.set(selection, btn);
    return btn;
  };

  // "All days" row.
  const allBtn = makeRow(ALL_DAYS);
  allBtn.classList.add('day-item--all');
  {
    const label = document.createElement('span');
    label.className = 'day-item__title';
    label.textContent = 'כל הימים';
    allBtn.appendChild(label);
    const count = document.createElement('span');
    count.className = 'day-item__count';
    count.textContent = `${itinerary.days.length} ימים`;
    allBtn.appendChild(count);
  }

  // One row per day.
  for (const day of itinerary.days) {
    const btn = makeRow(day.n);
    btn.appendChild(colorDot(categoryColor(day.category)));

    const body = document.createElement('span');
    body.className = 'day-item__body';

    const line1 = document.createElement('span');
    line1.className = 'day-item__meta';
    line1.textContent = `יום ${day.n} · ${day.dow} · ${shortDate(day.date)}`;
    body.appendChild(line1);

    const line2 = document.createElement('span');
    line2.className = 'day-item__title';
    const title = document.createElement('bdi'); // isolate any Latin in titles
    title.textContent = day.title;
    line2.appendChild(title);
    body.appendChild(line2);

    btn.appendChild(body);
  }

  const controller: DayListController = {
    setActive(selection) {
      for (const [sel, btn] of buttons) {
        const active = sel === selection;
        btn.classList.toggle('is-active', active);
        if (active) btn.setAttribute('aria-current', 'true');
        else btn.removeAttribute('aria-current');
      }
    },
  };
  return controller;
}
